import { Box } from "@mui/material";
import { PhotoProvider, PhotoView } from 'react-photo-view'
import 'react-photo-view/dist/react-photo-view.css'
import MetadataSidebar from './MetadataSidebar'
import { InfoOutlined, RotateLeft, RotateRight, Download, DeleteOutline } from "@mui/icons-material";
import { IconButton, Typography, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import StartIcon from '@mui/icons-material/Start';
import { format, parse } from "date-fns";
import { useLocalStorage } from "@northpowerdev/np-react-utils";


export default function UpdatedGalleryViewer({ documents, bffBase, remove, options }) {
  const [isOpen, setIsOpen] = useLocalStorage('metadataSidebarOpen', false)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))
  const isReadOnly = options?.readOnly ?? true

  const formatKey = (key) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const inputFormat = 'dd/MM/yy';
      const outputFormat = 'yyyy';
      const dateObject = parse(dateString, inputFormat, new Date());
      const formattedDate = format(dateObject, outputFormat);
      return formattedDate

    } catch (e) {
      return dateString
    }
  }

  const resolveUrl = (path) => {
    if (typeof path !== 'string') return ''
    if (path.startsWith('/')) return `${bffBase || ''}${path}`
    return path
  }

  // Process documents and insert date markers when metadata.created changes
  const processDocumentsWithDateMarkers = (docs) => {
    if (!docs || docs.length === 0) return []

    const processed = []
    let lastCreatedDate = null

    docs.forEach((doc) => {
      const currentCreatedDate = doc?.metadata?.["File Date"]
      console.log('currentCreatedDate: ', currentCreatedDate)
      const createdDateStr = currentCreatedDate ? formatDate(currentCreatedDate) : null

      // Insert date marker if the created date has changed
      if (createdDateStr && createdDateStr !== lastCreatedDate) {
        processed.push({
          documentPreview: '',
          thumbnail: '',
          url: '',
          type: 'text/plain',
          name: createdDateStr,
        })
        lastCreatedDate = createdDateStr
      }

      // Add the actual document
      processed.push({
        documentPreview: Array.isArray(doc.documentPreview) ? doc.documentPreview : resolveUrl(doc.documentPreview),
        thumbnail: resolveUrl(doc.thumbnail),
        url: resolveUrl(doc.url),
        type: doc.contentType,
        name: doc.fileName,
        fileKey: doc.fileKey,
        metadata: doc.metadata
      })
    })

    return processed
  }

  const imageMedia = processDocumentsWithDateMarkers(documents)

  // console.log('imageMedia', imageMedia)

  return (
    <Box sx={{ position: 'relative' }}>
      <PhotoProvider
        maskOpacity={0.5}
        loop={true}
        toolbarRender={({ onRotate, rotate, index }) => {
          const currentItem = imageMedia[index];
          return (
            <Box style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ position: 'absolute', top: 2, left: 30, transform: 'translateX(32px)', display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={() => setIsOpen(!isOpen)} >
                  <InfoOutlined sx={{ color: '#fff' }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentItem?.url && (
                  <a href={currentItem.url} download={currentItem.name || 'download'} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <IconButton size="small">
                      <Download sx={{ color: '#fff' }} />
                    </IconButton>
                  </a>
                )}
                <IconButton size="small" onClick={() => onRotate(rotate - 90)}>
                  <RotateLeft sx={{ color: '#fff' }} />
                </IconButton>
                <IconButton size="small" onClick={() => onRotate(rotate + 90)}>
                  <RotateRight sx={{ color: '#fff' }} />
                </IconButton>
              </Box>
            </Box>
          );
        }}
        overlayRender={({ index }) => (
          <MetadataSidebar open={isOpen} metadata={imageMedia[index] && imageMedia[index].metadata} />
        )}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
          {imageMedia?.map((item, index) => (
            <PhotoView
              key={index}
              src={item.type !== 'application/pdf' && item.type !== 'image/tiff' && item.type !== "text/plain" && item.type !== 'text/xml'  && item.type !== 'text/html' && item.type !== 'application/msword' && item.type !== 'application/octet-stream'  ? item.documentPreview : undefined}
              render={
                item.type === 'application/pdf'
                  ? ({ attrs }) => (
                    <Box id="pdf-viewer" style={{ display: 'flex', justifyContent: 'center', position: 'relative', top: '-45%', transform: 'translateX(-50%)', height: '95vh' }}>
                      <Typography variant="body2" align="center" sx={{ color: 'white', position: 'absolute', bottom: 10, left: 10, display: { xs: 'none', md: 'block' } }}>ctrl + scroll to zoom</Typography>
                      <Box
                        component="iframe"
                        title={item.name || 'document'}
                        src={renderSrcUrl(item?.documentPreview, item?.type)}
                        {...attrs}
                        allow="fullscreen"
                        style={{ ...attrs.style, width: isSmallScreen ? '80vw' : '60vw', height: '95vh', border: 0 }}
                      />
                    </Box>
                  )
                  : (item.type === 'text/html')
                  ? ({ attrs }) => (
                    <Box id="html-viewer" style={{ display: 'flex', justifyContent: 'center', position: 'relative', top: '-45%', transform: 'translateX(-50%)', height: '95vh' }}>
                      <Box
                        component="iframe"
                        title={item.name || 'document'}
                        src={renderSrcUrl(item?.thumbnail, item?.type)}
                        {...attrs}
                        allow="fullscreen"
                        style={{ ...attrs.style, width: isSmallScreen ? '80vw' : '60vw', height: '95vh', border: 0, backgroundColor: 'white' }}
                      />
                    </Box>
                  )
                  : (item.type === "text/plain")
                    ? ({ attrs }) => {
                      return (
                        <Box style={{
                          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative',
                          transform: 'translateX(-50%)', backgroundColor: '#414141', width: '50%'
                        }}>
                          <Box sx={{ m: 1, textAlign: 'center', display: 'flex', alignItems: 'center' }}>
                            <Typography variant="h4" align="center" sx={{ mr: 1, color: 'white' }} >
                              {item?.name}
                            </Typography>
                            <StartIcon color="primary" fontSize="large" />
                          </Box>
                        </Box>
                      )
                    }
                    : (item.type === "text/xml" || item.type === "text/plain" || item.type === "text/html" || item.type === "application/msword" || item.type === "image/tiff" || item.type === "application/octet-stream" || !item.type)
                      ? ({ attrs }) => {
                        return (
                          <Box style={{
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', top: '-45%',
                            transform: 'translateX(-50%)', backgroundColor: '#fff',
                          }}>
                            <Box sx={{ m: 1, textAlign: 'center' }}>
                              <Typography variant="h5" align="center">
                                {!item.type ? 'Empty Hyperlink Details' : `${formatKey(item.type.replace('/', ' '))} File Details`}
                              </Typography>
                              {item.type && (
                                <Typography variant="body1" align="center">
                                  Details for this file aren't available in this view.<br />
                                  <a href={item.url} target="_blank" rel="noopener noreferrer">Download is available if required.</a>
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ mt: 1, mb: 1, textAlign: 'left', width: '40%' }}>
                              {item.type && (
                                <>
                                  <Typography variant="body2"><strong>File Name:</strong> {item.name}</Typography>
                                  <Typography variant="body2"><strong>Content Type:</strong> {item.type}</Typography>
                                </>
                              )}
                              {Object.entries(item.metadata || {}).map(([key, value]) =>
                                value && (
                                  <Typography key={key} variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    <strong>{formatKey(key)}:</strong> {value}
                                  </Typography>
                                )
                              )}
                            </Box>
                          </Box>
                        )
                      }
                      : undefined
              }
            >

              {item.type === "text/plain" ?
                <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center', justifyContent: "center" }}>
                  <Typography>{item.name}</Typography>
                </Box>
                :
                <Box sx={{ position: 'relative' }}>
                  {typeof remove === 'function' && !isReadOnly && item?.fileKey && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        remove(item.fileKey)
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        zIndex: 2,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                      }}
                      aria-label="Delete"
                    >
                      <DeleteOutline sx={{ color: '#fff' }} fontSize="small" />
                    </IconButton>
                  )}
                  <Tooltip title={item.name || 'File Name'} arrow placement="top">
                    <img
                      src={resolveUrl(item?.thumbnail)}
                      alt={item.name}
                      style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                    />
                  </Tooltip>
                </Box>}

            </PhotoView>
          ))}
        </Box>
      </PhotoProvider >

    </Box >


  )
}

const renderSrcUrl = (documentPreview, type, bffBase) => {
  switch (type) {
    case 'application/pdf': {
      const preview = documentPreview + '#toolbar=0&page=1&view=fitH&zoom=page-width'
      return preview || ''
    }
    case 'image/jpeg':
    case 'image/png':
    case 'image/jpg': {
      return documentPreview || ''
    }
    default: {
      // console.log('unsupported file type', type)
      return documentPreview || ''
    }
  }
}

